import type { Item } from '@/lib/db/schema'
import { daysBetween, parseLocalDate } from '@/lib/dashboard-next-up'

/** Location filter value that matches items with no room set. */
export const NO_ROOM_KEY = '__none__'
export const NO_ROOM_LABEL = 'No room set'

/** Rooms with this many items or fewer are folded into "Everywhere else". */
export const SMALL_ROOM_MAX = 3
/** Tiles per shelf. The shelf's "See all" opens the full list. */
export const SHELF_ITEM_LIMIT = 12
/** Warranties ending within this many days surface as the attention card. */
export const WARRANTY_ATTENTION_DAYS = 30

export type OverviewItem = Pick<
  Item,
  'id' | 'name' | 'category' | 'location' | 'imageUrl' | 'warrantyExpiry' | 'purchaseDate'
>

export interface RoomShelf {
  /** Location filter value for "See all": the room label, or NO_ROOM_KEY. */
  key: string
  label: string
  count: number
  items: OverviewItem[]
}

export interface ItemsOverview {
  total: number
  attention: { item: OverviewItem; daysUntilExpiry: number } | null
  /** Rooms big enough for their own shelf, largest first. */
  rooms: RoomShelf[]
  /** Small rooms merged into one shelf. Null when there are none. */
  everywhereElse: Omit<RoomShelf, 'key'> | null
}

const byName = (a: OverviewItem, b: OverviewItem) =>
  a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })

/**
 * Group a property's items into room shelves for the mobile Items overview.
 * Rooms are matched case- and whitespace-insensitively (same rule as the list
 * API's location filter) and labelled with their most-used spelling.
 *
 * @param today YYYY-MM-DD in the user's timezone
 */
export function buildItemsOverview(rows: OverviewItem[], today: string): ItemsOverview {
  const groups = new Map<string, { items: OverviewItem[]; spellings: Map<string, number> }>()

  for (const row of rows) {
    const trimmed = row.location?.trim() ?? ''
    const key = trimmed ? trimmed.toLowerCase() : NO_ROOM_KEY
    let group = groups.get(key)
    if (!group) {
      group = { items: [], spellings: new Map() }
      groups.set(key, group)
    }
    group.items.push(row)
    if (trimmed) group.spellings.set(trimmed, (group.spellings.get(trimmed) ?? 0) + 1)
  }

  const shelves: (RoomShelf & { all: OverviewItem[] })[] = []
  for (const [key, group] of groups) {
    const label =
      key === NO_ROOM_KEY
        ? NO_ROOM_LABEL
        : [...group.spellings.entries()].sort((a, b) => b[1] - a[1])[0][0]
    shelves.push({
      key: key === NO_ROOM_KEY ? NO_ROOM_KEY : label,
      label,
      count: group.items.length,
      items: [...group.items].sort(byName).slice(0, SHELF_ITEM_LIMIT),
      all: group.items,
    })
  }

  // Largest rooms first; "No room set" always sits after real rooms.
  shelves.sort((a, b) => {
    if ((a.key === NO_ROOM_KEY) !== (b.key === NO_ROOM_KEY)) return a.key === NO_ROOM_KEY ? 1 : -1
    return b.count - a.count || a.label.localeCompare(b.label)
  })

  const rooms = shelves
    .filter((s) => s.count > SMALL_ROOM_MAX)
    .map(({ key, label, count, items }) => ({ key, label, count, items }))
  const smallItems = shelves.filter((s) => s.count <= SMALL_ROOM_MAX).flatMap((s) => s.all)

  return {
    total: rows.length,
    attention: findWarrantyAttention(rows, today),
    rooms,
    everywhereElse: smallItems.length
      ? {
          label: 'Everywhere else',
          count: smallItems.length,
          items: [...smallItems].sort(byName).slice(0, SHELF_ITEM_LIMIT),
        }
      : null,
  }
}

/** The item whose warranty ends soonest within the attention window, if any. */
export function findWarrantyAttention(
  rows: OverviewItem[],
  today: string
): ItemsOverview['attention'] {
  const todayDate = parseLocalDate(today)
  let best: ItemsOverview['attention'] = null
  for (const item of rows) {
    if (!item.warrantyExpiry) continue
    const days = daysBetween(parseLocalDate(item.warrantyExpiry), todayDate)
    if (days < 0 || days > WARRANTY_ATTENTION_DAYS) continue
    if (!best || days < best.daysUntilExpiry) best = { item, daysUntilExpiry: days }
  }
  return best
}

/** Days until a warranty ends, or null when there is none or it already ended. */
export function warrantyDaysLeft(warrantyExpiry: string | null, today: string): number | null {
  if (!warrantyExpiry) return null
  const days = daysBetween(parseLocalDate(warrantyExpiry), parseLocalDate(today))
  return days >= 0 ? days : null
}

/**
 * Patch a built overview by id: drop the item from whichever shelf shows it and
 * decrement that shelf's count. Lets a mutation update the mobile shelves the
 * moment it fires, before the refetch re-groups everything.
 *
 * - Delete: `decrementTotal` + `clearAttention` (the item is gone entirely).
 * - Move: neither (the item still exists with the same warranty; we only take it
 *   off its current shelf and let the refetch place it in the destination room).
 */
export function removeItemFromOverview(
  overview: ItemsOverview,
  id: string,
  { clearAttention, decrementTotal }: { clearAttention: boolean; decrementTotal: boolean }
): ItemsOverview {
  const drop = (items: OverviewItem[]) => items.filter((i) => i.id !== id)

  const rooms = overview.rooms.map((room) =>
    room.items.some((i) => i.id === id) ? { ...room, items: drop(room.items), count: room.count - 1 } : room
  )

  const ee = overview.everywhereElse
  const everywhereElse =
    ee && ee.items.some((i) => i.id === id) ? { ...ee, items: drop(ee.items), count: ee.count - 1 } : ee

  return {
    total: decrementTotal ? overview.total - 1 : overview.total,
    attention: clearAttention && overview.attention?.item.id === id ? null : overview.attention,
    rooms,
    everywhereElse,
  }
}
